import { moneyFormat } from 'src/utils/utility'
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppInfoContext from 'src/provider/state-manager/appInfoProvider';
import UserContext from 'src/provider/state-manager/userProvider';
import ApiContext from 'src/provider/API/call-service';
import { AbsoluteContainer, AppLabel, AppSpan, AppText, Circle, CustomContainer, FlexColumn, FlexRow, GridContainer, ScreenContainer } from 'src/style';
import { InsightChart, Table, TransactionDetailsD3 } from 'src/component'
import {IoChevronBackOutline, IoChevronForwardOutline} from 'react-icons/io5'
import {ReactComponent as BalanceTopIcon} from 'src/assets/img/balance-card-up-icon.svg'
import {expenseData, inflowData} from 'src/utils/mock-data'
import { insightColumns, insightData } from 'src/utils/tableLayout';
import helpers from 'src/utils/helpers';


export const InsightD3: React.FC = () => {
    const navigate = useNavigate()
    const {API} = useContext(ApiContext)
    const {info: {institutionData}} = useContext(AppInfoContext)
    const {user: {accountWithBalanceDetails}, setUserProperty} = useContext(UserContext)
    const [txData, setTxData] = useState<any>(null)
    const [accountIndex, setAccountIndex] = useState(0)
    const [isHideBalance, setIsHideBalance] = useState(false)
    const [isTxDetails, setIsTxDetails] = useState(false)
    const [navPosition, setNavPosition] = useState(0)
    const [chartPeriod, setChartPeriod] = useState('30 days')
    const [insightSummary, setInsightSummary] = useState<any>(expenseData)
    const [accounts, setAccounts] = useState<any>([])
    const [chartData, setChartData] = useState<any>({})
    const [monthlyExpense, setMonthlyExpense] = useState({lastMonthExpense: 0 , thisMonthExpense: 0})

    useEffect(() => {
        if (!accounts.length) setAccounts(accountWithBalanceDetails)
        if (chartPeriod && accountWithBalanceDetails.length) handleFetchExpenseSummary()
    }, [accountWithBalanceDetails, chartPeriod, accountIndex])


    async function handleFetchExpenseSummary () {
        const [duration, period] = chartPeriod.split(' ')

        const payload = {
            accountNumber: accountWithBalanceDetails[accountIndex]?.nuban,
            startDate: helpers.addPrevMomentToDate(Number(duration), period),
            endDate: new Date().toISOString()
        }

        const response = await API.getExpenseCategorySummary(payload)
        const spendings = await API.getMonthlyExpense(payload.accountNumber)

        if (response) {
            setInsightSummary(response)

            let [categories, amount, info]: any = [[], [], response.slice(0, 5)]
            for (let item of info) {
                categories.push(item.category)
                amount.push(item.total)
            }

            setChartData({categories, amount})
        }

        if (spendings) setMonthlyExpense(spendings)
    }

    async function handleViewAllTx (data: any) {
        data.accountNumber = accountWithBalanceDetails[accountIndex]?.nuban
        await setUserProperty('insightData', data)
        navigate(`/dashboard/insight/${data.category}`)
    }

    return (
        <>
            <ScreenContainer padding='2.5' topPadding='0'>
                <CustomContainer
                    customRadius='28px 28px 0px 0px' borderColor='#EAECF0'
                    minHeight='100' mnHUnit='%' bgColor='#FCFCFC' padding='2'
                    className='d2-screen-bg-img'
                >
                    <CustomContainer>
                        <AppSpan textSize='2.2' fontWeight='700'>Insight</AppSpan>
                    </CustomContainer>
                    <FlexRow 
                        gap='2.5' justifyContent='space-between'
                        alignItems='stretch' topMargin='2'
                        className='balance-card-container'
                    >
                        <CustomContainer 
                            width='48.5' sizeUnit='%' minHeight='15.75'
                            className='acct-balance-container'
                        >
                            <CustomContainer 
                                width='100' sizeUnit='%' minHeight='7.3' bgColor='#0D968F'
                                padding='0.7' leftPadding='2' rightPadding='2'
                                style={{
                                    zIndex: '2', borderTopLeftRadius: '1rem', 
                                    borderTopRightRadius: '1rem', borderBottom: '0.2rem solid rgba(180, 227, 225, 1)'
                                }}
                            >
                                <AbsoluteContainer top='0' right='0'>
                                    <BalanceTopIcon />
                                </AbsoluteContainer>

                                <FlexRow 
                                    width='auto' sizeUnit='' justifyContent='space-between'
                                >
                                    <AppText 
                                        textSize='1.2' fontWeight='600' color='#ffffff'
                                    >
                                        {accounts[accountIndex]?.name}
                                        <AppSpan textSize='1.2' fontWeight='600' color='#ffffff' leftPadding='1.5'>{accounts[accountIndex]?.nuban}</AppSpan>
                                    </AppText>
                                    <FlexRow 
                                        width='auto' sizeUnit=''
                                        style={{flexBasis: 'auto', flexShrink: '0', flexGrow: '0'}} 
                                    >
                                        <AppSpan 
                                            textSize='2.5' color='#ffffff' cursor='pointer' hoverColor='#A5A5A5'
                                            style={{visibility: accountIndex === 0 ? 'hidden' : 'visible'}}
                                            onClick={() => setAccountIndex(accountIndex - 1)}
                                        >
                                            <IoChevronBackOutline/>
                                        </AppSpan>
                                        <AppSpan 
                                            textSize='2.5' color='#ffffff' cursor='pointer' hoverColor='#A5A5A5'
                                            style={{visibility: (accountIndex + 1) === accounts?.length ? 'hidden' : 'visible'}}
                                            onClick={() => setAccountIndex(accountIndex + 1)}
                                        >
                                            <IoChevronForwardOutline/>
                                        </AppSpan>
                                    </FlexRow>
                                </FlexRow>
                            </CustomContainer>
                            <CustomContainer
                                width='100' sizeUnit='%' minHeight='6.5' 
                                padding='0.7' leftPadding='2' rightPadding='2'
                                bgColor='#1A2C37'
                                style={{zIndex: '2', borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem'}}
                            >
                                <FlexRow 
                                    gap='1.5' justifyContent='space-between' 
                                    wrap='wrap'
                                >
                                    <FlexColumn width='auto' sizeUnit='' alignItems='flex-start'>
                                        <AppLabel textSize='1.07' color='#ffffff'>Last Month Spend</AppLabel>
                                        <AppText 
                                            textSize='1.76' fontWeight='700' color='#ffffff' 
                                            topMargin='-0.4' fontFamily='Gilmer-Bold'
                                        >
                                            {moneyFormat(monthlyExpense?.lastMonthExpense, true,institutionData?.defaultCurrency)}
                                        </AppText>
                                    </FlexColumn>
                                    <FlexColumn width='auto' sizeUnit='' alignItems='flex-start'>
                                        <AppLabel textSize='1.07' color='#ffffff'>This Month Spend</AppLabel>
                                        <AppText 
                                            textSize='1.76' fontWeight='700' color='#ffffff' 
                                            topMargin='-0.4' fontFamily='Gilmer-Bold'
                                        >
                                           {moneyFormat(monthlyExpense?.thisMonthExpense, true,institutionData?.defaultCurrency)}
                                        </AppText>
                                    </FlexColumn>
                                </FlexRow>
                            </CustomContainer>
                            <GridContainer>
                                <FlexRow gap="0.8" topMargin="0.7" width='auto' sizeUnit=''>
                                    { accounts?.length >= 2 &&
                                        accounts?.map((item: any, index: number) => {
                                            return (
                                                <Circle size="0.8" bgColor="#0D968F" key={index}/>
                                            )
                                        })
                                    }
                                </FlexRow>
                            </GridContainer>
                        </CustomContainer>
                        
                        <CustomContainer 
                            width='48.5' sizeUnit='%' minHeight='15.75'
                            className='acct-card-container'
                        >
                            { insightSummary[0].category &&
                                <GridContainer 
                                    minHeight='4.6' radius='0.78' bgColor='#FFD795' 
                                    borderColor='#EAECF0' padding='1' topPadding='0.5' bottomPadding='0.5'
                                >
                                    <FlexRow justifyContent='space-between' gap='1.5'>
                                        <AppSpan textSize='1.2' color='#131A2D'>{insightSummary[0].category}</AppSpan>
                                        <AppText textSize='1.72' color='#131A2D' fontWeight='700'>{moneyFormat(insightSummary[0].total,false,institutionData?.defaultCurrency)}</AppText>
                                    </FlexRow>
                                </GridContainer>
                            }
                            { insightSummary[1].category &&
                                <GridContainer 
                                    minHeight='4.6' radius='0.78' bgColor='#83C7FF' topMargin='1'
                                    borderColor='#EAECF0' padding='1' topPadding='0.5' bottomPadding='0.5'
                                >
                                    <FlexRow justifyContent='space-between' gap='1.5'>
                                        <AppSpan textSize='1.2' color='#131A2D'>{insightSummary[1].category}</AppSpan>
                                        <AppText textSize='1.72' color='#131A2D' fontWeight='700'>{moneyFormat(insightSummary[1].total,false,institutionData?.defaultCurrency)}</AppText>
                                    </FlexRow>
                                </GridContainer>
                            }
                            { insightSummary[2].category &&
                                <GridContainer 
                                    minHeight='4.6' radius='0.78' bgColor='#7BDDD8' topMargin='1'
                                    borderColor='#EAECF0' padding='1' topPadding='0.5' bottomPadding='0.5'
                                >
                                    <FlexRow justifyContent='space-between' gap='1.5'>
                                        <AppSpan textSize='1.2' color='#131A2D'>{insightSummary[2].category}</AppSpan>
                                        <AppText textSize='1.72' color='#131A2D' fontWeight='700'>{moneyFormat(insightSummary[2].total,false,institutionData?.defaultCurrency)}</AppText>
                                    </FlexRow>
                                </GridContainer>
                            }
                        </CustomContainer>
                    </FlexRow>
                    <CustomContainer topMargin='4'>
                        <FlexRow width='auto' sizeUnit='' wrap='wrap' gap='1'>
                            <GridContainer 
                                width='9.3' height='3.6' radius='0.6'
                                bgColor={chartPeriod === '24 hours' ? '#E7F6F5' : 'transparent'}
                                onClick={() => setChartPeriod('24 hours')}
                            >
                                <AppText 
                                    fontWeight='600' cursor='pointer'
                                    color={chartPeriod === '24 hours' ? '#0A756F' : '#667085'}
                                >
                                    24 Hours
                                </AppText>
                            </GridContainer>
                            <GridContainer 
                                width='9.3' height='3.6' radius='0.6'
                                bgColor={chartPeriod === '7 days' ? '#E7F6F5' : 'transparent'}
                                onClick={() => setChartPeriod('7 days')}
                            >
                                <AppText 
                                    fontWeight='600' cursor='pointer'
                                    color={chartPeriod === '7 days' ? '#0A756F' : '#667085'}
                                >
                                    7 Days
                                </AppText>
                            </GridContainer>
                            <GridContainer 
                                width='9.3' height='3.6' radius='0.6'
                                bgColor={chartPeriod === '30 days' ? '#E7F6F5' : 'transparent'}
                                onClick={() => setChartPeriod('30 days')}
                            >
                                <AppText 
                                    fontWeight='600' cursor='pointer'
                                    color={chartPeriod === '30 days' ? '#0A756F' : '#667085'}
                                >
                                    30 Days
                                </AppText>
                            </GridContainer>
                            <GridContainer 
                                width='9.3' height='3.6' radius='0.6'
                                bgColor={chartPeriod === '6 months' ? '#E7F6F5' : 'transparent'}
                                onClick={() => setChartPeriod('6 months')}
                            >
                                <AppText 
                                    fontWeight='600' cursor='pointer'
                                    color={chartPeriod === '6 months' ? '#0A756F' : '#667085'}
                                >
                                    6 Months
                                </AppText>
                            </GridContainer>
                            <GridContainer 
                                width='9.3' height='3.6' radius='0.6'
                                bgColor={chartPeriod === '12 months' ? '#E7F6F5' : 'transparent'}
                                onClick={() => setChartPeriod('12 months')}
                            >
                                <AppText 
                                    fontWeight='600' cursor='pointer'
                                    color={chartPeriod === '12 months' ? '#0A756F' : '#667085'}
                                >
                                    12 Months
                                </AppText>
                            </GridContainer>
                        </FlexRow>
                        <CustomContainer topMargin='1.5' >
                            <FlexRow gap='1.5' wrap='wrap' bottomMargin='1.5'>
                                <FlexRow width='auto' sizeUnit=''>
                                    <Circle size='0.8' bgColor='#FFD795' rightMargin='0.7'/>
                                    <AppSpan color='#667085'>{insightSummary[0]?.category}</AppSpan>
                                </FlexRow>
                                <FlexRow width='auto' sizeUnit=''>
                                    <Circle size='0.8' bgColor='#83C7FF' rightMargin='0.7'/>
                                    <AppSpan color='#667085'>{insightSummary[1]?.category}</AppSpan>
                                </FlexRow>
                                <FlexRow width='auto' sizeUnit=''>
                                    <Circle size='0.8' bgColor='#7BDDD8' rightMargin='0.7'/>
                                    <AppSpan color='#667085'>{insightSummary[2]?.category}</AppSpan>
                                </FlexRow>
                                <FlexRow width='auto' sizeUnit=''>
                                    <Circle size='0.8' bgColor='#FFC0C0' rightMargin='0.7'/>
                                    <AppSpan color='#667085'>{insightSummary[3]?.category}</AppSpan>
                                </FlexRow>
                                <FlexRow width='auto' sizeUnit=''>
                                    <Circle size='0.8' bgColor='#B8FFBF' rightMargin='0.7'/>
                                    <AppSpan color='#667085'>{insightSummary[4]?.category}</AppSpan>
                                </FlexRow>
                            </FlexRow>
                            <CustomContainer height='30'>
                                <InsightChart 
                                    data={chartData.amount}
                                    label={chartData.categories}
                                />
                            </CustomContainer>
                        </CustomContainer>
                    </CustomContainer>
                    <CustomContainer
                        borderColor='#EAECF0' radius='0.8' topMargin='3'
                        shadow='0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)'
                    >
                        <FlexColumn minHeight='8' padding='2' alignItems='flex-start'>
                            <FlexRow justifyContent='space-between' gap='2'>
                                <AppText textSize='1.8' fontWeight='600' color='#101828'>Recent {navPosition === 0 ? 'Expense' : 'Inflow'}</AppText>
                            </FlexRow>
                        </FlexColumn>
                        <Table
                            tableColumns={insightColumns(handleViewAllTx, navPosition,institutionData?.defaultCurrency)}
                            tableData={insightSummary}
                            noDataText='No Transactions'
                            noRadius
                            customStyle
                        />
                    </CustomContainer>
                </CustomContainer>
            </ScreenContainer>
            { isTxDetails &&
                <TransactionDetailsD3
                    data={txData}
                    close={() => setIsTxDetails(false)} 
                />
            }
        </>
    );
}


